import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import { Shell } from "./components/shell";
import { Login } from "./pages/login";
import { SetPassword } from "./pages/set-password";
import Shop from "./pages/shop";

const Dashboard = lazy(() => import("./pages/dashboard"));
const Products = lazy(() => import("./pages/products"));
const ProductEdit = lazy(() => import("./pages/product-edit"));
const StockAdjust = lazy(() => import("./pages/stock-adjust"));
const Categories = lazy(() => import("./pages/categories"));
const Resellers = lazy(() => import("./pages/resellers"));
const Subscribers = lazy(() => import("./pages/subscribers"));
const Customers = lazy(() => import("./pages/customers"));
const CustomerCare = lazy(() => import("./pages/customer-care"));
const Purchasing = lazy(() => import("./pages/purchasing"));
const Finance = lazy(() => import("./pages/finance"));
const Orders = lazy(() => import("./pages/orders"));
const ManualOrder = lazy(() => import("./pages/manual-order"));
const ManualSale = lazy(() => import("./pages/manual-sale"));
const BatchSales = lazy(() => import("./pages/batch-sales"));
const Reconciliation = lazy(() => import("./pages/reconciliation"));
const StockAlerts = lazy(() => import("./pages/stock-alerts"));
const Logs = lazy(() => import("./pages/logs"));
const Settings = lazy(() => import("./pages/settings"));
const Analytics = lazy(() => import("./pages/analytics"));
const Staff = lazy(() => import("./pages/staff"));

function Guard({ session }: { session: Session | null }) { return session ? <Shell><Outlet /></Shell> : <Navigate to="/login" replace />; }
const Loading = () => <div className="loading" role="status">Loading operations data…</div>;

function AdminApp() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next);
      if (event === "PASSWORD_RECOVERY") setPasswordRecovery(true);
    });
    return () => data.subscription.unsubscribe();
  }, []);
  if (session === undefined) return <Loading />;
  if (passwordRecovery && window.location.pathname !== "/set-password") return <Navigate to="/set-password" replace />;
  return <Suspense fallback={<Loading />}><Routes>
    <Route path="/login" element={session ? <Navigate to="/" replace /> : <Login />} />
    <Route path="/set-password" element={<SetPassword session={session} onComplete={() => setPasswordRecovery(false)} />} />
    <Route element={<Guard session={session} />}>
      <Route index element={<Dashboard />} />
      <Route path="products" element={<Products />} />
      <Route path="products/:id" element={<ProductEdit />} />
      <Route path="products/:id/stock" element={<StockAdjust />} />
      <Route path="products/categories" element={<Categories />} />
      <Route path="categories" element={<Navigate to="/products/categories" replace />} />
      <Route path="resellers" element={<Resellers />} />
      <Route path="subscribers" element={<Subscribers />} />
      <Route path="customers" element={<Customers />} />
      <Route path="customer-care" element={<CustomerCare />} />
      <Route path="purchasing" element={<Purchasing />} />
      <Route path="finance" element={<Finance />} />
      <Route path="orders" element={<Orders />} />
      <Route path="orders/new" element={<ManualOrder />} />
      <Route path="orders/new-sale" element={<ManualSale />} />
      <Route path="orders/batch" element={<BatchSales />} />
      <Route path="orders/reconciliation" element={<Reconciliation />} />
      <Route path="reconciliation" element={<Navigate to="/orders/reconciliation" replace />} />
      <Route path="alerts" element={<StockAlerts />} />
      <Route path="logs" element={<Logs />} />
      <Route path="settings" element={<Settings />} />
      <Route path="analytics" element={<Analytics />} />
      <Route path="staff" element={<Staff />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes></Suspense>;
}

export function App() {
  return window.location.pathname === "/shop" ? <Shop /> : <AdminApp />;
}
